import React from 'react';

import * as styles from './styles';

import { type DateRange, type EventSeverity, EventTrigger, MediaType, Severity, type UUID } from '@cobliteam/common-types';

import { useGetMediaEventInfinity } from '@cobliteam/hooks/src/useCamera/useCameraMedia';

import { ContentLoader, Tag, WarningIcon } from '@cobliteam/base-components';

import { Typography } from '@cobliteam/ui/components';

import { getSeverityTagLabel } from '../../utils';

import { ReactComponent as MediumIcon } from './assets/medium.svg';
import { ReactComponent as SevereIcon } from './assets/severe.svg';
import { ReactComponent as VerySevereIcon } from './assets/very-severe.svg';

interface SeverityTagProps {
  severity: Severity;
  mediaPeriod?: DateRange;
  vehicleIds?: UUID[];
  driverIds?: UUID[];
  eventTriggers?: EventTrigger[];
  severities?: EventSeverity[];
  isFavorite?: boolean;
}

export const getMediaEventLabel = (eventTrigger: EventTrigger) => {
  switch (eventTrigger) {
    case EventTrigger.HARSH_BREAKING:
      return 'Frenagem brusca';
    case EventTrigger.HARSH_ACCELERATION:
      return 'Aceleração brusca';
    case EventTrigger.HARSH_CORNERING:
      return 'Curva brusca';
    case EventTrigger.TRAFFIC_SPEED_VIOLATED:
      return 'Velocidade da via excedida';
    case EventTrigger.STOP_SIGN_VIOLATED:
      return 'Placa de PARE não respeitada';
    case EventTrigger.LANE_DRIFT:
      return 'Mudança brusca de faixa';
    case EventTrigger.TAILGATING:
      return 'Distância insegura';
    case EventTrigger.ANOMALY:
      return 'Anomalia';
    case EventTrigger.DISTRACTED_DRIVING:
      return 'Direção distraída';
    case EventTrigger.YAWN:
      return 'Bocejo';
    case EventTrigger.EYES_CLOSED:
      return 'Olhos fechados';
    case EventTrigger.PHONE_USAGE:
      return 'Uso de celular';
    case EventTrigger.SMOKING:
      return 'Fumando ao volante';
    case EventTrigger.FRONT_COLLISION_WARNING:
      return 'Alerta de colisão frontal';
    case EventTrigger.MAX_SPEED_EXCEEDED:
      return 'Velocidade máxima excedida';
    case EventTrigger.SOS:
      return 'Botão do motorista';
    default:
      return 'Desconhecido';
  }
};

export const getMediaSeverityClass = (severity: Severity) => {
  switch (severity) {
    case Severity.VERY_SEVERE:
      return styles.verySeriousInfractionTag;
    case Severity.SEVERE:
      return styles.seriousInfractionTag;
    case Severity.MEDIUM:
      return styles.mediumInfractionTag;
    default:
      return styles.verySeriousInfractionTag;
  }
};

const StatusShapeIcon = {
  [Severity.MEDIUM]: () => <MediumIcon fontSize='inherit' />,
  [Severity.SEVERE]: () => <SevereIcon fontSize='inherit' />,
  [Severity.VERY_SEVERE]: () => <VerySevereIcon fontSize='inherit' />,
};

export const SeverityCountTag = React.memo(({ severity, severities, mediaPeriod, vehicleIds, driverIds, eventTriggers, isFavorite }: SeverityTagProps) => {
  const { data, isLoading } = useGetMediaEventInfinity({
    page: 0,
    size: 1,
    mediaTypes: [MediaType.EVENT_VIDEO],
    vehicleIds: vehicleIds,
    favoriteMedia: isFavorite,
    driverIds: driverIds,
    mediaPeriod: mediaPeriod,
    eventTriggers: eventTriggers,
    severityTypes: severities,
  });

  if (isLoading) {
    return (
      <ContentLoader width={110} height={30} key={1}>
        <rect x='0' y='0' width='100' height='25' />
      </ContentLoader>
    );
  }

  const count = data?.pages[0]?.totalElements ?? 0;

  const severityLabel = getSeverityTagLabel(severity, count);

  return (
    <div className='flex items-center space-x-2'>
      {StatusShapeIcon[severity]()}
      <Typography variant='description-list-default' color='secondary'>
        {severityLabel}
      </Typography>
    </div>
  );
});

export const SeverityTag: React.FC<SeverityTagProps> = React.memo(({ severity }: SeverityTagProps) => {
  const severityLabel = getSeverityTagLabel(severity);
  const className = getMediaSeverityClass(severity);

  return (
    <div className={styles.eventTriggerTag}>
      <Tag className={className} Icon={WarningIcon} label={severityLabel} />
    </div>
  );
});
